import { Node } from 'prosemirror-model'
import { Command, EditorState, Plugin } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

import { getMarkExtent } from '#util'

import { isInTitlePage } from '../titlePageView'
import { PluginFactory } from '../types'

import { handleKeyDown, markSelectionAsDeletion } from './commands'
import {
  createAdditionMark,
  handlePaste,
  handleTextInput,
  isAddition,
  isProposed,
  isTracked,
  markChangesKey,
  MarkChangesState,
  TrackedChangeInfo,
} from './helpers'

import styles from './markChanges.module.css'

const classNames = {
  selectedTrackedChange: styles['selected-tracked-change'],
} as const

export const markChangesPlugin: PluginFactory = () => {
  return new Plugin<MarkChangesState>({
    key: markChangesKey,
    appendTransaction(transactions, oldState, editorState) {
      const remoteTr = transactions.some((t) => t.getMeta('collab$'))
      if (remoteTr) return null

      const changed = !oldState.doc.eq(editorState.doc)
      if (changed) return null

      if (isInTitlePage(editorState.selection.$to.pos, editorState)) {
        return null
      }

      const { tr } = editorState
      const start = tr.doc.resolve(tr.selection.$to.pos - 1)
      tr.setStoredMarks([createAdditionMark(start)])

      return tr
    },
    state: {
      init(_, state) {
        return {
          selectedId: undefined,
          decorations: DecorationSet.empty,
          trackedChanges: scanDocumentForTrackedChanges(state.doc),
          localChanges: new Set(),
        }
      },
      apply(tr, state) {
        const meta = tr.getMeta(markChangesKey)
        const isSelChange = meta?.type === 'select-change'
        const selectedId = isSelChange ? meta.id : state.selectedId

        // if the document has not changed, we only need to recompute decorations
        // if the active selected change mark has been updated
        if (!tr.docChanged) {
          const decorations = isSelChange
            ? buildSelectedDecorations(tr.doc, selectedId)
            : state.decorations
          return { ...state, decorations, selectedId }
        }

        const decorations = buildSelectedDecorations(tr.doc, selectedId)
        const trackedChanges = scanDocumentForTrackedChanges(tr.doc)

        // if the doc has changed as a result of a remote edit,
        // we recalc decos and tracked changes
        if (tr.getMeta('collab$')) {
          return { ...state, decorations, trackedChanges }
        }

        // *local* edits require a little creativity to account for each and every change

        // when a user backspaces over a single character and then stops
        // or marks a deletion by typing over the top of it
        // we have to decorate the tr with extra metadata because the
        // selection does not touch the mark by the time it is applied
        const localChanges = new Set(state.localChanges)
        if (meta?.type === 'local-changes') localChanges.add(meta.id)

        // we expect all other tracked changes to touch the current tr selection
        const changeIds = tr.selection.$from
          .marks()
          .concat(tr.selection.$to.marks())
          .filter((m) => isTracked(m) && isProposed(m))
          .map((m) => m.attrs.id)

        changeIds.forEach((id) => {
          localChanges.add(id)
        })

        return { ...state, decorations, localChanges, trackedChanges }
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)?.decorations
      },
      handleKeyDown,
      handleTextInput,
      handlePaste,
      handleDOMEvents: {
        // this closes the loophole that was previously allowing untracked deletions to sneak through
        // but the behavior isnt actually correct
        // TODO: reimplement PMs default clipboard hydration *without* deleting the initial selection
        // https://discuss.prosemirror.net/t/handlecut-property/1135/10
        cut: (view, evt) => {
          const handled = markSelectionAsDeletion(view.state, view.dispatch)
          if (handled) evt.preventDefault()
          return handled
        },
      },
    },
  })
}

function buildSelectedDecorations(doc: Node, selectedId?: string) {
  if (!selectedId) return DecorationSet.empty
  const decorations: Decoration[] = []
  doc.descendants((node, pos) => {
    if (!node.isText) return true
    const mark = node.marks.find((mark) => mark.attrs.id === selectedId)
    if (!mark) return true
    // if the proposal is approved or rejected,
    // we want to skip decorating it because the change marker is no longer visible in the editor
    // and the presence of the decoration would just be confusing
    if (mark.attrs.status === 'approved' || mark.attrs.status === 'rejected') {
      return true
    }

    const start = pos
    const end = start + (node.text?.length ?? 1)

    decorations.push(
      Decoration.inline(start, end, {
        class: classNames.selectedTrackedChange,
      }),
    )
  })

  return DecorationSet.create(doc, decorations)
}

function scanDocumentForTrackedChanges(
  doc: Node,
): Map<string, TrackedChangeInfo[]> {
  const trackedChanges = new Map<string, TrackedChangeInfo[]>()

  doc.descendants((node, pos) => {
    if (!node.isText) return true

    const trackedMarks = node.marks.filter(isTracked)

    for (const mark of trackedMarks) {
      const id = mark.attrs.id
      if (!id) continue

      const $pos = doc.resolve(pos)
      const { from, to } = getMarkExtent($pos, mark)
      const snippet = doc.textBetween(from, to)

      const changes = trackedChanges.get(id) ?? []
      const change: TrackedChangeInfo = {
        id,
        changeType: isAddition(mark) ? 'addition' : 'deletion',
        snippet,
        from,
        to,
      }
      trackedChanges.set(id, [...changes, change])
    }

    return true
  })

  return trackedChanges
}

export function getSelectedId(state: EditorState) {
  return markChangesKey.getState(state)?.selectedId
}

export function getTrackedChanges(
  state: EditorState,
): Map<string, TrackedChangeInfo[]> {
  return markChangesKey.getState(state)?.trackedChanges ?? new Map()
}

export function selectTrackedChange(id: string): Command {
  return (state, dispatch) => {
    dispatch?.(state.tr.setMeta(markChangesKey, { type: 'select-change', id }))
    return true
  }
}

export function getLocalChanges(state: EditorState): Set<string> {
  return markChangesKey.getState(state)?.localChanges ?? new Set()
}
